/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ActionGroup } from "client-code/actions";
import { PreviewGroup } from "client-code/previews";
import type {
  TActionsVodOfferParams,
  TCustomActionList,
  TPreview,
  TPreviewServiceGetVodOfferPreviewParamsV2,
  VodOttActionDetailsList
} from "@tivocorp/data-domain-lib";

import { MetadataType, IdType } from "../enums";
import { PlaybackError } from "../error";
import type {
  IMetadata,
  IMetadataModel,
  IVodMetadataParams,
  IVodPlaybackModel,
  TMetadataModelParams
} from "../interfaces";
import { Log } from "../../logging";
import type { TNetworkOptions } from "../../types";
import {
  ActionsInteractor,
  PreviewInteractor,
  VodInteractor
} from "../../interactor";

/**
 * actionType value used by the action-details service (client-code/action-details)
 * to identify the primary VOD watch action within a watchActionsList response.
 */
const VOD_WATCH_ACTION_TYPE = "watchFromVodAction";

type VodWatchActionKernel = Record<string, unknown> & { offerId?: string };

/**
 * Metadata model implementation for VOD (Video On Demand) content.
 *
 * Handles fetching and storing metadata for VOD playback:
 * - ACTION_DETAILS: resolves the primary watch action (and its offerId) for
 *   the VOD title via the VOD OTT action-details service. The endpoint is
 *   chosen by `vodPlaybackModel.idType` (content, offer or unified
 *   collection). This is the only step playback depends on: the watch action
 *   names the provider (DRM) and carries the session-create identifiers.
 * - VOD_ACTION_LIST: onepass/bookmark actions for the resolved VOD offer.
 * - PREVIEW: standard VOD offer preview (metadata, linear, onDemand,
 *   vodStatus) for the resolved offer.
 * - FULL_INFO_PREVIEW: the additional groups (casts, description), merged
 *   into the standard preview.
 *
 * VOD_ACTION_LIST and the previews only feed the UI.
 *
 * ACTION_DETAILS must be prepared before VOD_ACTION_LIST, PREVIEW, or
 * FULL_INFO_PREVIEW, since those derive the vodOfferId from the watch action
 * resolved in that step.
 *
 * @implements {IMetadataModel}
 */
export class VodMetadataModelImpl implements IMetadataModel {
  private static readonly TAG: string = "VodMetadataModelImpl";

  private static readonly METHOD_PREPARE_METADATA_BY_TYPE: string =
    "prepareMetadataByType(): ";

  private static readonly ACTION_LIST_GROUPS: ActionGroup[] = [
    ActionGroup.ScheduleOnepass,
    ActionGroup.ScheduleBookmark
  ];

  private static readonly STANDARD_PREVIEW_GROUPS: PreviewGroup[] = [
    PreviewGroup.Metadata,
    PreviewGroup.Linear,
    PreviewGroup.OnDemand,
    PreviewGroup.VodStatus
  ];

  /**
   * Groups the full-info view adds on top of the standard preview. Fetched
   * on their own and merged, rather than re-requesting the standard groups
   * alongside them.
   */
  private static readonly ADDITIONAL_PREVIEW_GROUPS: PreviewGroup[] = [
    PreviewGroup.Casts,
    PreviewGroup.Description
  ];

  /**
   * Every VOD metadata call belongs to the playback, not to the screen that
   * started it. Starting playback is followed by a route change to the
   * player, and NetworkGuard aborts in-flight calls on navigation unless they
   * opt out — which would cancel the action-details call the whole flow
   * waits on. SocuMetadataModelImpl opts out for the same reason.
   */
  private static readonly NETWORK_OPTIONS: TNetworkOptions = {
    bypassNavigationCancellation: true
  };

  /**
   * Action details are always fetched fresh (forced past the domain-lib
   * cache). The watch action's kernel carries the title's saved position,
   * which changes every time it is watched, and that cache lives for the whole
   * app session — a cached response would resume a replay from wherever the
   * first launch found it rather than where the viewer left off.
   */
  private static readonly FORCE_FRESH_ACTION_DETAILS: boolean = true;

  private readonly mMetadata: IMetadata = {};

  private mError: PlaybackError = new PlaybackError("", "");

  private readonly mActionsInteractor: ActionsInteractor =
    new ActionsInteractor();

  private readonly mPreviewInteractor: PreviewInteractor =
    new PreviewInteractor();

  private readonly mVodInteractor: VodInteractor = new VodInteractor();

  private isVodParams(
    params: TMetadataModelParams
  ): params is IVodMetadataParams {
    return (
      params.metadataType === MetadataType.ACTION_DETAILS ||
      params.metadataType === MetadataType.VOD_ACTION_LIST ||
      params.metadataType === MetadataType.PREVIEW ||
      params.metadataType === MetadataType.FULL_INFO_PREVIEW
    );
  }

  /**
   * Reads the vodOfferId off the previously resolved watch action.
   * ACTION_DETAILS must have run first to populate this.
   */
  private getVodOfferId(): string {
    const watchVodAction = this.mMetadata.watchVodAction;
    if (watchVodAction === undefined) {
      return "";
    }
    const kernel = watchVodAction.kernel as VodWatchActionKernel | undefined;
    return kernel !== undefined && typeof kernel.offerId === "string"
      ? kernel.offerId
      : "";
  }

  /**
   * Fetches the action details from the endpoint matching the id's kind,
   * as Kaveri's `VodPlayAction.getVodOttActionDetails` does.
   */
  private fetchActionDetails(
    vodPlaybackModel: IVodPlaybackModel
  ): Promise<VodOttActionDetailsList> {
    const {
      id,
      idType,
      contentSupplierPartnerId,
      collapsedVodItemId,
      videoProviderId
    } = vodPlaybackModel;

    if (idType === IdType.OFFER) {
      return this.mVodInteractor.fetchOttOfferActionDetails(
        { offerId: id, isCatchUp: false, isStartOver: false },
        VodMetadataModelImpl.FORCE_FRESH_ACTION_DETAILS,
        VodMetadataModelImpl.NETWORK_OPTIONS
      );
    }

    if (idType === IdType.UNIFIED_COLLECTION) {
      return this.mVodInteractor.getUnifiedCollectionActionDetails(
        {
          unifiedCollectionId: id,
          contentSupplierPartnerId,
          collapsedVodItemId,
          videoProviderId
        },
        VodMetadataModelImpl.FORCE_FRESH_ACTION_DETAILS,
        VodMetadataModelImpl.NETWORK_OPTIONS
      );
    }

    return this.mVodInteractor.fetchOttContentActionDetails(
      {
        contentId: id,
        contentSupplierPartnerId,
        collapsedVodItemId,
        videoProviderId
      },
      VodMetadataModelImpl.FORCE_FRESH_ACTION_DETAILS,
      VodMetadataModelImpl.NETWORK_OPTIONS
    );
  }

  private async retrieveActionDetails(
    vodPlaybackModel: IVodPlaybackModel
  ): Promise<void> {
    try {
      const response: VodOttActionDetailsList =
        await this.fetchActionDetails(vodPlaybackModel);

      const watchVodAction = (response.watchActionsList || []).find(
        (action) => action.actionType === VOD_WATCH_ACTION_TYPE
      );

      if (watchVodAction === undefined) {
        throw new Error("No VOD watch action available");
      }

      this.mMetadata.watchVodAction =
        watchVodAction as unknown as IMetadata["watchVodAction"];
    } catch (error) {
      Log.e(VodMetadataModelImpl.TAG, "retrieveActionDetails()", error);
      this.mError = new PlaybackError(
        "VOD_ACTION_DETAILS_FETCH_ERROR",
        "Failed to fetch VOD action details",
        "Watch Action Unavailable",
        "Unable to retrieve the watch action for this title."
      );
      throw this.mError;
    }
  }

  private async retrieveActionList(): Promise<void> {
    try {
      const params: TActionsVodOfferParams = {
        vodOfferId: this.getVodOfferId(),
        groups: VodMetadataModelImpl.ACTION_LIST_GROUPS
      };

      const response: TCustomActionList =
        await this.mActionsInteractor.fetchVodOfferActions(
          params,
          false,
          VodMetadataModelImpl.NETWORK_OPTIONS
        );

      this.mMetadata.actions = response.actions;
    } catch (error) {
      Log.e(VodMetadataModelImpl.TAG, "retrieveActionList()", error);
      this.mError = new PlaybackError(
        "VOD_ACTION_LIST_FETCH_ERROR",
        "Failed to fetch VOD action list",
        "Actions Unavailable",
        "Unable to retrieve available actions for this title."
      );
      throw this.mError;
    }
  }

  private async fetchVodOfferPreview(
    groups: PreviewGroup[]
  ): Promise<TPreview> {
    try {
      const params: TPreviewServiceGetVodOfferPreviewParamsV2 = {
        vodOfferId: this.getVodOfferId(),
        groups
      };

      return await this.mPreviewInteractor.fetchVodOfferPreviewV2(
        params,
        false,
        VodMetadataModelImpl.NETWORK_OPTIONS
      );
    } catch (error) {
      Log.e(VodMetadataModelImpl.TAG, "fetchVodOfferPreview()", error);
      this.mError = new PlaybackError(
        "VOD_PREVIEW_FETCH_ERROR",
        "Failed to fetch VOD offer preview",
        "Preview Unavailable",
        "Unable to retrieve preview information for this title."
      );
      throw this.mError;
    }
  }

  private async retrieveStandardPreview(): Promise<void> {
    this.mMetadata.vodOfferPreview = await this.fetchVodOfferPreview(
      VodMetadataModelImpl.STANDARD_PREVIEW_GROUPS
    );
  }

  /**
   * Adds the full-info groups to the standard preview. The standard preview
   * is fetched first when it has not been loaded yet, so the merged result is
   * always a complete preview rather than casts and description alone.
   */
  private async retrieveFullPreview(): Promise<void> {
    if (this.mMetadata.vodOfferPreview === undefined) {
      await this.retrieveStandardPreview();
    }

    const additional: TPreview = await this.fetchVodOfferPreview(
      VodMetadataModelImpl.ADDITIONAL_PREVIEW_GROUPS
    );

    this.mMetadata.vodOfferPreview = VodMetadataModelImpl.mergePreview(
      this.mMetadata.vodOfferPreview || {},
      additional
    );
  }

  /**
   * Overlays the fields `additional` actually delivers onto `base`. A field
   * the additional response leaves undefined keeps the standard value
   * instead of erasing it.
   */
  private static mergePreview(base: TPreview, additional: TPreview): TPreview {
    const merged: Record<string, unknown> = { ...base };
    const delivered = additional as Record<string, unknown>;

    Object.keys(delivered).forEach((key: string) => {
      if (delivered[key] !== undefined) {
        merged[key] = delivered[key];
      }
    });

    return merged as TPreview;
  }

  public getMetadata(): IMetadata {
    return this.mMetadata;
  }

  public getError(): PlaybackError {
    return this.mError;
  }

  public async prepareMetadataByType(
    params: TMetadataModelParams
  ): Promise<void> {
    if (!this.isVodParams(params)) {
      const errorMessage = "Invalid params for VodMetadataModelImpl";
      Log.e(
        VodMetadataModelImpl.TAG,
        VodMetadataModelImpl.METHOD_PREPARE_METADATA_BY_TYPE,
        errorMessage
      );
      this.mError = new PlaybackError(
        "INVALID_PARAMS",
        errorMessage,
        "Invalid Parameters",
        "The metadata parameters are not valid for VOD content."
      );
      throw this.mError;
    }

    const { metadataType, playable } = params;

    if (metadataType === MetadataType.ACTION_DETAILS) {
      const vodPlaybackModel =
        playable === undefined ? undefined : playable.vodPlaybackModel;
      if (vodPlaybackModel === undefined) {
        const errorMessage = "Missing vodPlaybackModel for VOD metadata";
        Log.e(
          VodMetadataModelImpl.TAG,
          VodMetadataModelImpl.METHOD_PREPARE_METADATA_BY_TYPE,
          errorMessage
        );
        this.mError = new PlaybackError(
          "MISSING_VOD_PLAYBACK_MODEL",
          errorMessage,
          "Invalid Parameters",
          "The metadata parameters are not valid for VOD content."
        );
        throw this.mError;
      }
      await this.retrieveActionDetails(vodPlaybackModel);
    } else if (metadataType === MetadataType.VOD_ACTION_LIST) {
      await this.retrieveActionList();
    } else if (metadataType === MetadataType.PREVIEW) {
      await this.retrieveStandardPreview();
    } else if (metadataType === MetadataType.FULL_INFO_PREVIEW) {
      await this.retrieveFullPreview();
    } else {
      const errorMessage = `Unsupported metadata type for VodMetadataModelImpl: ${metadataType}`;
      Log.e(
        VodMetadataModelImpl.TAG,
        VodMetadataModelImpl.METHOD_PREPARE_METADATA_BY_TYPE,
        errorMessage
      );
      this.mError = new PlaybackError(
        "UNSUPPORTED_METADATA_TYPE",
        errorMessage
      );
      throw this.mError;
    }
  }
}
